import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";

import { DOTPayItem } from "../../../domain/contracts/DOTPayItem";
import { PayItem } from "../../../domain/contracts/PayItem";
import { Quantity } from "../../../domain/contracts/Quantity";
import { Funding } from "../../../domain/contracts/Funding";
import { Cost } from "../../../domain/contracts/Cost";
import { Time } from "../../../domain/contracts/Time";
import { ConstructionPhase } from "../../../domain/contracts/ConstructionPhase";
import { PlanPrep } from "../../../domain/contracts/PlanPrep";
import { Tabulation } from "../../../domain/contracts/Tabulation";
import { Asset } from "../../../domain/assets/Asset";
import { QAQC } from "../../../domain/contracts/QAQC";
import { Help } from "../../../domain/common/Help";
import { Remarks } from "../../../domain/common/Remarks";

// your existing per-piece helpers:
import { queryPayItemForElement } from "./payItemQueries";
import { queryQuantityForElement } from "./quantityQueries";
import { queryFundingForElement } from "./fundingQueries";
import { queryCostForElement } from "./costQueries";
import { queryTimeForElement } from "./timeQueries";
import { queryConstructionPhaseForElement } from "./constructionPhaseQueries";
import { queryPlanPrepForElement } from "./planPrepQueries";
import { queryTabulationForElement } from "./tabulationQueries";
import { queryAssetForElement } from "./assetQueries";
import { queryQAQCForElement } from "./qaqcQueries";
import { queryHelpForElement } from "./helpQueries";
import { queryRemarksForElement } from "./remarksQueries";

/**
 * Valid DOT_PayItem "slots" (DOT_PayItem1..DOT_PayItem5).
 */
export type DOTPayItemIndex = 1 | 2 | 3 | 4 | 5;

const DOTPAYITEM_SCHEMA =
  "DgnCustomItemTypes_DOT_DigitalAssetMetadata";

/**
 * Builds the root ECSQL for a given DOT_PayItem index.
 * Example for index = 1:
 *   FROM DgnCustomItemTypes_DOT_DigitalAssetMetadata.DOT_PayItem1ElementAspect p
 */
function buildDotPayItemRootQuery(index: DOTPayItemIndex): string {
  if (index < 1 || index > 5) {
    throw new Error(`Invalid DOT_PayItem index: ${index}`);
  }

  return `
    SELECT
      p.SPECBOOK_FILTER AS SPECBOOK_FILTER,
      p.REFITEM_SEARCH  AS REFITEM_SEARCH
    FROM ${DOTPAYITEM_SCHEMA}.DOT_PayItem${index}ElementAspect p
    WHERE p.Element.Id = ?
  `;
}

/**
 * Queries a single DOT_PayItem{index} "slot" (1..5) for a given element.
 * Returns undefined if there is no aspect row for that index on the element.
 */
export async function queryDOTPayItemForElement(
  iModel: IModelConnection,
  elementId: string,
  index: DOTPayItemIndex,
): Promise<DOTPayItem | undefined> {
  const bindings = QueryBinder.from([elementId]);

  // 1) Root fields (SPECBOOK_FILTER, REFITEM_SEARCH)
  let reader: ReturnType<IModelConnection["createQueryReader"]>;
  try {
    reader = iModel.createQueryReader(
      buildDotPayItemRootQuery(index),
      bindings,
      { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
    );
  } catch (err) {
    const msg = (err as Error).message ?? String(err);
    if (msg.includes("ECClass") && msg.includes("does not exist")) {
      // Schema missing in this iModel; treat as no data for this slot.
      return undefined;
    }
    throw err;
  }

  let specbookFilter = "";
  let refitemSearch = "";

  // for await (const row of reader) {
  //   const plain = row.toRow() as any;
  //   specbookFilter = plain.SPECBOOK_FILTER ?? "";
  //   refitemSearch = plain.REFITEM_SEARCH ?? "";
  // }

  // // If there was no aspect row at all for this index, bail out
  // if (!specbookFilter && !refitemSearch) {
  //   return undefined;
  // }

  //SS Fix, testing 01-12-2026
  let found = false;

  try {
    for await (const row of reader) {
      found = true;
      const plain = row.toRow() as any;
      specbookFilter = plain.SPECBOOK_FILTER ?? "";
      refitemSearch = plain.REFITEM_SEARCH ?? "";
    }
  } catch (err) {
    const msg = (err as Error).message ?? String(err);
    if (msg.includes("ECClass") && msg.includes("does not exist")) {
      // Schema missing in this iModel; treat as no data for this slot.
      return undefined;
    }
    throw err;
  }

  if (!found) return undefined;


  // 2) All the sub-objects (run in parallel for speed).
  //    Each helper should now also accept the index so it can query
  //    DOT_PayItem{index}... as needed.
  const [
    payItem,
    quantity,
    funding,
    cost,
    time,
    constructionPhase,
    planPrep,
    tabulation,
    asset,
    qaqc,
    help,
    remarks,
  ] = await Promise.all([
    queryPayItemForElement(iModel, elementId, index),
    queryQuantityForElement(iModel, elementId, index),
    queryFundingForElement(iModel, elementId, index),
    queryCostForElement(iModel, elementId, index),
    queryTimeForElement(iModel, elementId, index),
    queryConstructionPhaseForElement(iModel, elementId, index),
    queryPlanPrepForElement(iModel, elementId, index),
    queryTabulationForElement(iModel, elementId, index),
    queryAssetForElement(iModel, elementId, index),
    queryQAQCForElement(iModel, elementId, index),
    queryHelpForElement(iModel, elementId, index),
    queryRemarksForElement(iModel, elementId, index),
  ]);

  // 3) Build the aggregate DOTPayItem, using default empty objects where needed.
  // If you extend DOTPayItem to carry the index, you can add `index` below.
  return new DOTPayItem({
    Index: index,
    SPECBOOK_FILTER: specbookFilter,
    REFITEM_SEARCH: refitemSearch,
    PayItem: payItem ?? PayItem.fromObject({}),
    Quantity: quantity ?? Quantity.fromObject({}),
    Funding: funding ?? Funding.fromObject({}),
    Cost: cost ?? Cost.fromObject({}),
    Time: time ?? Time.fromObject({}),
    ConstructionPhase:
      constructionPhase ?? ConstructionPhase.fromObject({}),
    PlanPrep: planPrep ?? PlanPrep.fromObject({}),
    Tabulation: tabulation ?? Tabulation.fromObject({}),
    Asset: asset ?? Asset.fromObject({}),
    QAQC: qaqc ?? QAQC.fromObject({}),
    Help: help ?? Help.fromObject({}),
    Remarks: remarks ?? Remarks.fromObject({}),
  });
}

/**
 * Convenience helper: query all DOT_PayItem1..5 "slots" for an element.
 * Returns only the ones that actually exist for that element.
 */
export async function queryAllDOTPayItemsForElement(
  iModel: IModelConnection,
  elementId: string,
): Promise<DOTPayItem[]> {
  const indices: DOTPayItemIndex[] = [1, 2, 3, 4, 5];

  const results = await Promise.all(
    indices.map(async (idx) => {
      try {
        return await queryDOTPayItemForElement(iModel, elementId, idx);
      } catch (err) {
        const msg = (err as Error).message ?? String(err);
        if (msg.includes("ECClass") && msg.includes("does not exist")) {
          return undefined;
        }
        throw err;
      }
    })
  );

  // Filter out undefined entries (indices with no aspect attached).
  return results.filter(
    (item): item is DOTPayItem => item !== undefined
  );
}

